import {
    ChangeEventHandler,
    KeyboardEventHandler,
    MouseEventHandler,
    ReactNode,
    useRef,
} from 'react'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import cl from 'clsx'

import TrashIcon from '@synapex/frontend-shared/icons/trash-14x16.svg?react'

import {
    type ICreative,
    type UseCreativeUpdateNameById,
    type UseCreativeDeleteById,
    type UseSetLinkCreativeMutation,
} from '@/shared/api'
import { AccepptSVGIcon, CancelCloseSVGIcon } from '@/shared/assets'
import { PencilIcon, SvgIconComponent } from '@/shared/icons'
import { cn } from '@/true-fsd/shared/utils/css'
import { Check as CheckIcon } from 'lucide-react'

import { type UseEditCreativeItem } from './useEditCreativeItem'
import classes from './CreativeSelect.module.scss'
import { useOnClickOutside } from 'usehooks-ts'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/shared/ui/tooltip'

type TitleInputProps = {
    initialTitle: string
    value: string
    onChange: ChangeEventHandler<HTMLInputElement>
    onEnter: (value: string) => void
    onEscape: () => void
}
const TitleInput = ({
    initialTitle,
    value,
    onChange,
    onEnter,
    onEscape,
}: TitleInputProps) => {
    const onApply = () => {
        if (initialTitle === value) {
            onEscape()
        } else {
            onEnter(value)
        }
    }

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        switch (e.key) {
            case 'Enter': {
                onApply()
                break
            }
            case 'Escape': {
                onEscape()
                break
            }
        }
    }

    const inputRef = useRef<HTMLInputElement>(null)
    useOnClickOutside(inputRef, onApply, 'mouseup')

    return (
        <input
            ref={inputRef}
            autoFocus
            {...{ value, onChange }}
            onKeyDown={onKeyDown}
            className="h-[28px] w-full max-w-[148px] flex-shrink-0 rounded-[8px] bg-[#F2F6FD] px-[12px] py-[8px] text-[10px] outline-none"
        />
    )
}

type TitleViewProps = {
    onClick?: () => void
    extended_title: boolean
    children: ReactNode
}
const TitleView = ({ children, extended_title, onClick }: TitleViewProps) => {
    return (
        <span
            className={cn(
                'text-[10px]',
                extended_title && 'flex h-full items-center'
            )}
            onClick={onClick}
        >
            {children}
        </span>
    )
}

type DateNodeProps = {
    onClick?: () => void
    value: Date | string
}
const DateNode = ({ onClick, value }: DateNodeProps) => {
    return (
        <span
            className="text-nowrap text-[13px] font-light text-secondary"
            onClick={onClick}
        >
            {format(new Date(value), 'dd.MM HH:mm')}
        </span>
    )
}

type IconButtonProps = {
    onClick?: MouseEventHandler
    Icon: SvgIconComponent
    selected?: boolean
    disabled?: boolean
}
const IconButton = ({
    onClick,
    Icon,
    selected,
    disabled = false,
}: IconButtonProps) => {
    const button = (
        <button
            {...(onClick && !disabled ? { onClick } : { disabled: true })}
            className={cl(classes.iconButton, {
                [classes.selected]: selected,
                [classes.disabled]: disabled,
            })}
        >
            <Icon className={classes.icon} />
        </button>
    )

    if (!disabled) {
        return button
    }

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent
                    side="top"
                    className="text-center text-[10px] leading-[14px]"
                >
                    Нельзя удалить если креатив используется
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}

type ConfirmDeleteProps = {
    onAccept: () => void
    onCancel: () => void
}
const ConfirmDelete = ({ onAccept, onCancel }: ConfirmDeleteProps) => {
    const ref = useRef(null)
    useOnClickOutside(ref, onCancel, 'mouseup')

    return (
        <div className={'flex items-center gap-[7px]'} ref={ref}>
            <span className="text-xs">Удалить?</span>

            <AccepptSVGIcon onClick={onAccept} />
            <div onClick={onCancel}>
                <CancelCloseSVGIcon />
            </div>
        </div>
    )
}

export type CreativeItemProps = {
    active?: boolean
    data: ICreative

    onSelectActiveCreative?: (newActive: ICreative) => void

    updateNameByIdMutate: UseCreativeUpdateNameById['mutate']
    deleteByIdMutate: UseCreativeDeleteById['mutate']
    setLinkCreativeMutate: UseSetLinkCreativeMutation['mutate']
} & UseEditCreativeItem

export const CreativeItem = ({
    active = false,
    data,

    onChangeTitle,
    onStartEdit,
    onStartDelete,
    onReset,
    editState,

    onSelectActiveCreative,

    deleteByIdMutate,
    setLinkCreativeMutate,
    updateNameByIdMutate,
}: CreativeItemProps) => {
    const effectiveTitle =
        data.extended_title ?? data.caption ?? data.text ?? ''

    if (editState.type !== 'idle' && editState.id === data.id) {
        //this edited or deleted

        //let mutation cache to be updated
        const deferredReset = () => setTimeout(onReset)

        switch (editState.type) {
            case 'edit': {
                const onTitleEnter = (value: string) => {
                    updateNameByIdMutate({ creativeId: data.id, name: value })
                    deferredReset()
                }

                return (
                    <Layout
                        {...{ data, active }}
                        title={
                            <TitleInput
                                initialTitle={data.extended_title ?? ''}
                                value={editState.value}
                                onChange={onChangeTitle}
                                onEscape={onReset}
                                onEnter={onTitleEnter}
                            />
                        }
                        mid={<DateNode value={data.created_at} />}
                        right={
                            <>
                                <IconButton Icon={PencilIcon} selected={true} />
                                <IconButton Icon={TrashIcon} />
                            </>
                        }
                    />
                )
            }
            case 'delete': {
                const onDeleteApply = () => {
                    deleteByIdMutate({ creativeId: data.id })
                    deferredReset()
                }

                return (
                    <Layout
                        {...{ data, active }}
                        title={
                            <TitleView
                                extended_title={Boolean(data.extended_title)}
                            >
                                {effectiveTitle}
                            </TitleView>
                        }
                        right={
                            <ConfirmDelete
                                onAccept={onDeleteApply}
                                onCancel={deferredReset}
                            />
                        }
                    />
                )
            }
        }
    }

    //noone edited or deleted

    const anotherItemEdited = editState.type === 'edit'
    const anotherItemDeleted = editState.type === 'delete'

    const allowedToDelete = data.active_in_links.length === 0

    // console.log({ anotherItemEdited, anotherItemDeleted })

    const onDeleteClick: MouseEventHandler | undefined = anotherItemEdited
        ? undefined
        : (e) => {
              e.stopPropagation()
              onStartDelete(data.id)
          }

    const onEditClick =
        anotherItemEdited || anotherItemDeleted
            ? undefined
            : data.isEditable
              ? () => {
                    // console.log('onEditClick')
                    onStartEdit(data.id, effectiveTitle)
                }
              : () => toast.error('Креатив не редактируемый')

    const onClickSelectActive =
        active || anotherItemEdited || anotherItemDeleted
            ? undefined
            : () => {
                  setLinkCreativeMutate({ creative: data })
                  if (onSelectActiveCreative) {
                      onSelectActiveCreative(data)
                  }
              }

    return (
        <Layout
            {...{ data, active, onImageClick: onClickSelectActive }}
            title={
                <TitleView
                    onClick={onClickSelectActive}
                    extended_title={Boolean(data.extended_title)}
                >
                    {effectiveTitle}
                </TitleView>
            }
            mid={
                <DateNode
                    value={data.created_at}
                    onClick={onClickSelectActive}
                />
            }
            right={
                <>
                    <IconButton Icon={PencilIcon} onClick={onEditClick} />
                    <IconButton
                        Icon={TrashIcon}
                        onClick={onDeleteClick}
                        disabled={!allowedToDelete}
                    />
                </>
            }
        />
    )
}

type LayoutProps = {
    editing?: boolean
    active?: boolean

    title: ReactNode
    mid?: ReactNode
    right: ReactNode

    data: ICreative
    onImageClick?: () => void
}
const Layout = ({
    editing = false,
    active = false,
    title,
    mid,
    right,
    data,
    onImageClick,
}: LayoutProps) => {
    return (
        <div
            className={cn(
                'relative flex w-full cursor-pointer items-center justify-between gap-[10px] rounded-[7px] px-[10px] py-[7px] hover:bg-[#F2F6FD]',
                editing || (active && 'bg-[#F2F6FD]'),
                editing && 'hover:bg-inherit'
            )}
        >
            <div onClick={onImageClick} className="relative">
                {active && (
                    <div className="absolute bottom-[17px] left-[17px] flex h-[11px] w-[11px] items-center justify-center rounded-full bg-blue-primary">
                        <CheckIcon width={6} height={6} color="#fff" />
                    </div>
                )}
                <img
                    src={data.preview_url || '/img/placeholder.svg'}
                    alt=""
                    className="h-[25px] min-w-[25px] max-w-[25px] rounded object-cover"
                />
            </div>

            <span className="line-clamp-2 h-[28px] w-full max-w-[148px] items-center text-[10px] leading-tight">
                {title}
            </span>

            {mid}

            <div className="flex items-center gap-[7px]">{right}</div>
        </div>
    )
}
